import { forwardRef } from "react";
import type { ButtonHTMLAttributes } from "react";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "tab";
export type ButtonSize = "sm" | "md";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** For variant="tab": whether this is the currently-selected tab. */
  active?: boolean;
}

const BASE = "inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

const SIZE: Record<ButtonSize, string> = {
  md: "px-4 py-2 text-sm",
  sm: "px-3 py-1.5 text-sm",
};

const VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-ledger-600 hover:bg-ledger-500 text-paper shadow-sm shadow-black/30",
  secondary: "border border-ledger-500 text-ledger-400 hover:bg-ledger-500/10",
  ghost: "border border-ink-700 text-muted hover:border-ledger-500 hover:text-paper",
  tab: "border border-ink-700 text-muted hover:border-ledger-500/50 hover:text-paper",
};

const TAB_ACTIVE = "border border-ledger-500 text-ledger-400 bg-ledger-500/10";

/**
 * Central button styling — variants mirror the classNames that used to be
 * hand-duplicated per component (primary CTA, outlined secondary, plain
 * outlined "ghost", and the Recover scenario tabs). Consolidating here so
 * button treatment can't quietly drift between pages.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", active = false, className = "", type = "button", ...props },
  ref
) {
  const variantClass = variant === "tab" && active ? TAB_ACTIVE : VARIANT[variant];
  return (
    <button
      ref={ref}
      type={type}
      className={`${BASE} ${SIZE[size]} ${variantClass} ${className}`}
      {...props}
    />
  );
});
